import { useState, type FormEvent } from "react";
import { apiErrorMessage, type Me, type RaceApi } from "../api";
import {
  CATEGORY_OPTIONS,
  profileFormValues,
  profileUpdateFromForm,
  validateProfileForm,
  type ProfileFormErrors,
  type ProfileFormValues,
} from "../auth/profile";
import { categoryRule } from "../race/format";
import { AppHeader } from "./AppHeader";

interface ProfileScreenProps {
  api: RaceApi;
  me: Me;
  reason: string | null;
  onSaved(me: Me): void;
  onCancel(): void;
  onHome(): void;
  onHistory(): void;
  onSignOut(): void;
}

export function ProfileScreen({
  api,
  me,
  reason,
  onSaved,
  onCancel,
  onHome,
  onHistory,
  onSignOut,
}: ProfileScreenProps) {
  const [values, setValues] = useState<ProfileFormValues>(() => profileFormValues(me));
  const [errors, setErrors] = useState<ProfileFormErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const update = <K extends keyof ProfileFormValues>(
    key: K,
    value: ProfileFormValues[K],
  ): void => {
    setValues((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: undefined }));
  };

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    const nextErrors = validateProfileForm(values);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setSaving(true);
    setFormError(null);
    try {
      const updated = await api.updateProfile(profileUpdateFromForm(values));
      onSaved(updated);
    } catch (cause) {
      setFormError(apiErrorMessage(cause));
    } finally {
      setSaving(false);
    }
  };

  const ready = Boolean(
    values.displayName.trim() && values.weightKg.trim() && values.heightCm.trim(),
  );

  return (
    <main className="page-screen profile-screen">
      <AppHeader
        me={me}
        onHome={onHome}
        onSignIn={onCancel}
        onSignOut={onSignOut}
        onProfile={() => undefined}
        onHistory={onHistory}
        compact
      />

      <div className="profile-layout">
        <div className="profile-copy">
          <p className="eyebrow">
            <span className="eyebrow__line" />
            Rider profile
          </p>
          <h1 className="auth-title">Set up your rider.</h1>
          <p className="auth-sub">
            Weight and height feed the physics; FTP and category set your race
            eligibility. You can change these later.
          </p>
          {reason ? <p className="auth-reason">{reason}</p> : null}
          {!ready ? (
            <p className="profile-warning" role="status">
              A display name, weight, and height are required before your first race.
            </p>
          ) : null}

          <div className="profile-facts">
            <div>
              <span>Account</span>
              <strong>{me.user.email ?? me.user.name ?? "Signed in"}</strong>
            </div>
            <div>
              <span>Category</span>
              <strong>
                {values.category} · {categoryRule(values.category)}
              </strong>
            </div>
            <div>
              <span>Races completed</span>
              <strong>{me.profile?.racesCompleted ?? 0}</strong>
            </div>
          </div>
        </div>

        <form
          className="profile-form"
          data-testid="profile-form"
          onSubmit={(event) => void submit(event)}
          noValidate
        >
          <div className="form-grid">
            <label className="field field--wide">
              <span className="field__label">Display name</span>
              <input
                className="field__input"
                name="displayName"
                autoComplete="nickname"
                placeholder="Ada"
                value={values.displayName}
                onChange={(event) => update("displayName", event.target.value)}
              />
              {errors.displayName ? (
                <span className="field__error">{errors.displayName}</span>
              ) : (
                <span className="field__hint">Shown on results and the live board.</span>
              )}
            </label>

            <label className="field">
              <span className="field__label">Weight (kg)</span>
              <input
                className="field__input"
                name="weightKg"
                type="number"
                inputMode="decimal"
                min={30}
                max={250}
                step="0.1"
                placeholder="72"
                value={values.weightKg}
                onChange={(event) => update("weightKg", event.target.value)}
              />
              {errors.weightKg ? (
                <span className="field__error">{errors.weightKg}</span>
              ) : (
                <span className="field__hint">30–250 kg.</span>
              )}
            </label>

            <label className="field">
              <span className="field__label">Height (cm)</span>
              <input
                className="field__input"
                name="heightCm"
                type="number"
                inputMode="decimal"
                min={100}
                max={250}
                step="1"
                placeholder="178"
                value={values.heightCm}
                onChange={(event) => update("heightCm", event.target.value)}
              />
              {errors.heightCm ? (
                <span className="field__error">{errors.heightCm}</span>
              ) : (
                <span className="field__hint">100–250 cm.</span>
              )}
            </label>

            <label className="field">
              <span className="field__label">FTP estimate (W)</span>
              <input
                className="field__input"
                name="ftpWatts"
                type="number"
                inputMode="numeric"
                min={0}
                max={2500}
                step="1"
                placeholder="250"
                value={values.ftpWatts}
                onChange={(event) => update("ftpWatts", event.target.value)}
              />
              {errors.ftpWatts ? (
                <span className="field__error">{errors.ftpWatts}</span>
              ) : (
                <span className="field__hint">Optional. Used for the category estimate.</span>
              )}
            </label>

            <label className="field">
              <span className="field__label">Category</span>
              <select
                className="field__input"
                name="category"
                value={values.category}
                onChange={(event) =>
                  update("category", event.target.value as ProfileFormValues["category"])
                }
              >
                {CATEGORY_OPTIONS.map((category) => (
                  <option key={category} value={category}>
                    {category} — {categoryRule(category)}
                  </option>
                ))}
              </select>
              {errors.category ? (
                <span className="field__error">{errors.category}</span>
              ) : (
                <span className="field__hint">
                  Self-select for your first three races; the server recalculates after.
                </span>
              )}
            </label>
          </div>

          <label className="consent">
            <input
              type="checkbox"
              checked={values.healthDataConsent}
              onChange={(event) => update("healthDataConsent", event.target.checked)}
            />
            <span>
              I consent to Cimara storing power, heart-rate, and body measurements for
              race results and category calculation.
            </span>
          </label>
          <p className="field__hint">
            Read the <a href="/privacy.html" target="_blank" rel="noreferrer">privacy policy and terms</a>.
          </p>

          {formError ? (
            <p className="form-error" role="alert">
              {formError}
            </p>
          ) : null}

          <div className="form-actions">
            <button
              type="submit"
              className="ride-button ride-button--compact"
              disabled={saving}
            >
              {saving ? "Saving…" : "Save profile"}
            </button>
            <button type="button" className="text-button" onClick={onCancel}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </main>
  );
}
